/**
 * Why a pitch did not reach the CheckUp — as something the citizen can act on.
 *
 * Every failure used to end in the same `copilot.videoUploadFailed` toast, so a
 * citizen whose video was simply too long, whose connection had stopped sending,
 * or whose file was over the storage limit was told only that "upload failed" —
 * and retried the same doomed thing (F13, F39, N17). Each case now has its own
 * remedy and its own copy.
 *
 * Classification only: the facts come from typed errors — the SDK's
 * `UploadStalledError` / `UploadTooLargeError` and the server's
 * `CHECKUP_PITCH_TOO_LONG` / `CHECKUP_PITCH_UNREADABLE` — never from matching
 * message text.
 */

import { DOMAIN_ERROR_CODES } from '@tonnie/foundation';
import { TonnieApiError, UploadStalledError, UploadTooLargeError } from '@tonnie/sdk';

export type PitchFailure =
  'too_long' | 'unreadable' | 'upload_stalled' | 'file_too_large' | 'upload_failed';

/** The locale key that explains each failure. */
export const PITCH_FAILURE_KEYS = {
  too_long: 'copilot.pitch.tooLong',
  unreadable: 'copilot.pitch.unreadable',
  upload_stalled: 'copilot.pitch.uploadStalled',
  file_too_large: 'copilot.pitch.fileTooLarge',
  upload_failed: 'copilot.videoUploadFailed',
} as const satisfies Record<PitchFailure, string>;

export function classifyPitchFailure(err: unknown): PitchFailure {
  if (err instanceof UploadStalledError) return 'upload_stalled';
  if (err instanceof UploadTooLargeError) return 'file_too_large';
  if (err instanceof TonnieApiError && err.code === DOMAIN_ERROR_CODES.CHECKUP_PITCH_TOO_LONG) {
    return 'too_long';
  }
  if (err instanceof TonnieApiError && err.code === DOMAIN_ERROR_CODES.CHECKUP_PITCH_UNREADABLE) {
    return 'unreadable';
  }
  return 'upload_failed';
}

/**
 * Expected outcomes are not defects: a too-long file, a dead connection and an
 * oversize file are the citizen's circumstances, reported at warning level so
 * their RATE stays visible without paging anyone. Only `upload_failed` — the
 * failure nobody classified — is an exception.
 */
export function isExpectedPitchFailure(failure: PitchFailure): boolean {
  return failure !== 'upload_failed';
}
